import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../App.css';

const UserLayout = ({ children, pageTitle = 'Dashboard', pageSubtitle = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.user-dropdown')) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [profileDropdownOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSidebarToggle = () => {
    // Don't collapse sidebar in mobile view
    if (window.innerWidth <= 768) {
      return;
    }
    setSidebarOpen(!sidebarOpen);
  };

  const menuItems = [
    { 
      icon: 'fi fi-rr-apps',
      label: 'Dashboard', 
      path: '/dashboard', 
      active: location.pathname === '/dashboard'
    },
    { 
      icon: 'fi fi-rr-user',
      label: 'My Profile', 
      path: `/profile/${user?.username}`,
      active: location.pathname === `/profile/${user?.username}`
    },
    { 
      icon: 'fi fi-rr-user-add',
      label: 'Create Profile', 
      path: '/create-profile',
      active: location.pathname === '/create-profile'
    },
    { 
      icon: 'fi fi-rr-edit',
      label: 'Edit Profile', 
      path: '/edit-profile',
      active: location.pathname === '/edit-profile'
    },
    { 
      icon: 'fi fi-rr-clipboard',
      label: 'View Responses', 
      path: '/dashboard#responses',
      active: location.hash === '#responses'
    },
    { 
      icon: 'fi fi-rr-palette',
      label: 'Templates', 
      path: '/templates',
      active: location.pathname === '/templates'
    },
    { 
      icon: 'fi fi-rr-settings',
      label: 'Settings', 
      path: '/settings',
      active: location.pathname === '/settings'
    }
  ];

  return (
    <div className="simple-admin-dashboard">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay" 
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}

      {/* Left Sidebar - Simple Design */}
      <aside className={`simple-sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo">
            <div className="logo-icon">{user?.username?.charAt(0).toUpperCase() || 'U'}</div>
            {(sidebarOpen || mobileMenuOpen) && <span className="logo-text">Dashboard</span>}
          </Link>
          <button 
            className="sidebar-toggle-btn"
            onClick={handleSidebarToggle}
            aria-label="Toggle sidebar"
          >
            <i className="fi fi-rr-angle-left"></i>
          </button>
        </div>
        
        <nav className="sidebar-nav">
          <ul className="nav-menu">
            {menuItems.map((item, index) => (
              <li key={index} className={`nav-item ${item.active ? 'active' : ''}`}>
                <Link
                  to={item.path}
                  className="nav-link"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    if (item.path.includes('#responses')) {
                      e.preventDefault();
                      window.location.hash = '#responses';
                      setTimeout(() => {
                        const responsesSection = document.getElementById('responses');
                        if (responsesSection) {
                          responsesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    } else if (item.path.includes('#feedbacks')) {
                      e.preventDefault();
                      window.location.hash = '#feedbacks';
                      setTimeout(() => {
                        const feedbacksSection = document.getElementById('feedbacks');
                        if (feedbacksSection) {
                          feedbacksSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }
                  }}
                >
                  <i className={item.icon}></i>
                  {(sidebarOpen || mobileMenuOpen) && <span>{item.label}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Top Header - Simple Design */}
      <header className={`simple-header ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
        <div className="header-content">
          <div className="header-left">
            <button 
              className="mobile-menu-btn" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
              aria-label="Toggle menu"
            >
              <i className="fi fi-rr-menu-burger"></i>
            </button>
            <div className="page-title">
              <h1>{pageTitle}</h1>
              <p>{pageSubtitle || `Welcome back, ${user?.username || 'User'}!`}</p>
            </div>
          </div>
          
          <div className="header-right">
            <div className="header-search">
              <i className="fi fi-rr-search"></i>
              <input type="text" placeholder="Search..." />
            </div>
            <button className="header-icon-btn" title="Notifications">
              <i className="fi fi-rr-bell"></i>
              <span className="badge">3</span>
            </button>
            <div className={`user-dropdown ${profileDropdownOpen ? 'open' : ''}`}>
              <button 
                className="user-btn" 
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              >
                <div className="user-avatar-small">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="user-name-small">{user?.username || 'User'}</span>
                <i className="fi fi-rr-angle-small-down"></i>
              </button>
              {profileDropdownOpen && (
                <div className="dropdown-menu">
                  <Link to={`/profile/${user?.username}`} onClick={() => setProfileDropdownOpen(false)}>
                    <i className="fi fi-rr-user"></i> My Profile
                  </Link>
                  <Link to="/settings" onClick={() => setProfileDropdownOpen(false)}>
                    <i className="fi fi-rr-settings"></i> Settings
                  </Link>
                  <div className="dropdown-divider"></div>
                  <button onClick={() => { setProfileDropdownOpen(false); handleLogout(); }}>
                    <i className="fi fi-rr-sign-out"></i> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Simple Design */}
      <main className={`simple-main-content ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
        <div className="content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};

export default UserLayout;

